import { Auth } from 'aws-amplify';

import type { AuthService } from 'ionic-appauth';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

import { AuthTokenType } from 'Components/Auth/types';
import { logger } from 'utils/logger/logger'; // Have to get it from the file directly because the index file causes circular dependency
import { OktaExternalAuth } from 'utils/okta/OktaExternalAuthService';
import { OktaInternalAuth } from 'utils/okta/OktaInternalAuthService';
import { getAuthTokenTypeLocalStorage } from './authToken';

/**
 * Get a valid Bearer Token.
 *
 * If the bearer token has expired, or will expire within the given buffer, a new token will be fetched using the
 * refresh token.
 */
export const getToken = async (
    /**
     * How many additional seconds the token should be valid for.
     *
     * This can be to allow some time in between getting the
     * token, making the request, the request reaching the server and the token being validated by the server.
     *
     * @default 15
     */
    buffer = 15
): Promise<string> => {
    let tokenType;
    try {
        tokenType = getAuthTokenTypeLocalStorage();
        const getAccessToken = async (authInstance: AuthService) => {
            const tokenResponse = await authInstance.getValidToken(-buffer); // Undocumented but the buffer is in seconds and need to be negative to get a token that is valid for longer
            return tokenResponse.accessToken;
        };
        switch (tokenType) {
            case AuthTokenType.OktaExternal:
                return getAccessToken(await OktaExternalAuth.getInstance());
            case AuthTokenType.OktaInternal:
                return getAccessToken(await OktaInternalAuth.getInstance());
            default:
                const currentSession = await Auth.currentSession(); // INSPECT: Should validate that this refreshes the token if required. Should also check that if we can give it the buffer.
                const { idToken } = currentSession as CognitoUserSession & { idToken: { jwtToken: string } }; // INSPECT: Wtf, TypeScript says idToken does not exist on this? It is definitely working

                return idToken.jwtToken;
        }
    } catch (error) {
        logger.error('Failed to get token', { error, tokenType });
        return ''; // INSPECT: Should we throw an error here? Or return undefined? Returning empty string is a bit weird, because presumably anything that calls getToken will be broken if it doesn't get a token.
    }
};
